<script setup>
import { computed, ref, watch } from 'vue'
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js/min'

/**
 * A phone number, with the country it belongs to.
 *
 * The field was a bare `type="tel"` box validated only as "not empty", so a
 * tutor typed too many digits and the team could not reach her — the number was
 * stored exactly as mistyped and nothing questioned it. A fixed maximum would
 * not have helped: a UK mobile is ten digits after the dial code and a Dutch one
 * nine, so any single limit is wrong somewhere.
 *
 * libphonenumber-js carries Google's own metadata and answers both questions —
 * whether this country's numbers can be this long, and whether what has been
 * typed is a real one. Digits beyond the country's maximum are refused as they
 * are typed, which is the actual complaint.
 *
 * The value handed back is E.164 ("+447911123456"), unambiguous wherever it is
 * later read. The UI is this project's own: the field sits among inputs styled
 * by our tokens, and a packaged widget would bring CSS that matches neither
 * them nor dark mode.
 */
const props = defineProps({
  modelValue: { type: String, default: '' },
  defaultCountry: { type: String, default: 'GB' },
  invalid: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

/** The countries a UK tutoring platform actually sees, then everywhere else. */
const PREFERRED = ['GB', 'IE', 'US', 'CA', 'AU', 'NZ', 'FR', 'DE', 'ES', 'IT']

const displayNames = typeof Intl !== 'undefined' && Intl.DisplayNames
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null

const countryName = (code) => displayNames?.of(code) ?? code

const countries = computed(() => {
  const all = getCountries()
  const preferred = PREFERRED.filter((code) => all.includes(code))
  const rest = all
    .filter((code) => !preferred.includes(code))
    .sort((a, b) => countryName(a).localeCompare(countryName(b)))

  return [...preferred, ...rest]
})

const initial = parsePhoneNumberFromString(props.modelValue || '')
const country = ref(initial?.country ?? props.defaultCountry)
const national = ref(initial?.formatNational() ?? props.modelValue ?? '')

const asE164 = () => {
  const number = parsePhoneNumberFromString(national.value || '', country.value)

  return number ? number.number : ''
}

/**
 * Kept in step with a value that arrives after the field has rendered — which
 * is how a saved draft loads on the application form.
 */
watch(() => props.modelValue, (value) => {
  if (!value || value === asE164()) return

  const next = parsePhoneNumberFromString(value)

  if (next) {
    country.value = next.country ?? country.value
    national.value = next.formatNational()
  }
})

const emitValue = () => {
  // Falls back to what was typed so a half-finished number survives an edit;
  // whether it is acceptable is settled by validation, not by this component.
  emit('update:modelValue', asE164() || national.value.trim())
}

const onInput = (event) => {
  const element = event.target

  /**
   * Refuse the keystroke that would make the number too long for its country,
   * rather than accepting it and complaining later. `validatePhoneNumberLength`
   * reports TOO_LONG only once the extra digit is genuinely one too many.
   */
  if (validatePhoneNumberLength(element.value, country.value) === 'TOO_LONG') {
    element.value = national.value

    return
  }

  national.value = new AsYouType(country.value).input(element.value)

  // Written straight back, because reformatting means the text in the box is
  // not the text that was typed and Vue will not re-render when the formatted
  // result happens to equal what the element already holds.
  element.value = national.value
  emitValue()
}

const onCountryChange = (event) => {
  country.value = event.target.value
  national.value = new AsYouType(country.value).input(national.value)
  emitValue()
}

const isValid = computed(() => {
  if (!national.value.trim()) return null

  return isValidPhoneNumber(national.value, country.value)
})

const showInvalid = computed(() => props.invalid || isValid.value === false)

defineExpose({ isValid: () => isValid.value === true })
</script>

<template>
  <div>
    <!--
      One control, not two.
      The country and the number are a single value, and showing them as two
      boxes side by side made the field look like two questions. The border,
      background and focus ring belong to the wrapper; the select and the input
      sit inside it with none of their own, so it reads and highlights as one
      field. `focus-within` puts the ring on the wrapper whichever half has the
      caret.
    -->
    <div
      :class="[
        'flex items-stretch rounded-xl border-2 bg-white transition-all focus-within:border-transparent focus-within:ring-2 focus-within:ring-brand-blue',
        showInvalid ? 'border-red-300 bg-red-50' : 'border-gray-200',
      ]"
    >
      <select
        :value="country"
        aria-label="Country dialling code"
        class="shrink-0 rounded-l-xl border-0 bg-transparent py-4 pl-4 pr-1 text-gray-700 outline-none focus:ring-0"
        @change="onCountryChange"
      >
        <option v-for="code in countries" :key="code" :value="code">
          {{ code }} +{{ getCountryCallingCode(code) }}
        </option>
      </select>

      <span class="my-3 w-px shrink-0 bg-gray-200" aria-hidden="true"></span>

      <input
        :value="national"
        type="tel"
        inputmode="tel"
        autocomplete="tel-national"
        :aria-invalid="showInvalid"
        placeholder="07700 900000"
        class="w-full min-w-0 rounded-r-xl border-0 bg-transparent px-4 py-4 text-gray-900 outline-none focus:ring-0"
        @input="onInput"
      >
    </div>

    <p v-if="isValid === false" class="mt-1 text-sm text-red-500">
      That does not look like a valid {{ countryName(country) }} phone number.
    </p>
  </div>
</template>
